import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { BedDouble, Bath, Ruler, ArrowUpRight, Clock3, MapPin, Loader2, Home } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import PageHeader, { BackLink } from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";

const SITE = "https://lagooncityrealestate.ca";

const fmtPrice = (p) => (p ? `$${Number(p).toLocaleString("en-CA")}` : "");
const listedLabel = (t) => {
  if (!t) return "For Sale";
  const days = Math.max(0, Math.round((Date.now() / 1000 - Number(t)) / 86400));
  if (days === 0) return "Listed today";
  if (days === 1) return "Listed yesterday";
  return `Listed ${days} days ago`;
};
const sqftLabel = (s) => (s ? (String(s).match(/\d/) && !String(s).toLowerCase().includes("ft") ? `${s} sq ft` : s) : "");

export default function ListingDetail() {
  const { key } = useParams();
  const [listing, setListing] = useState(null);
  const [status, setStatus] = useState("loading");
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/listings/${key}`)
      .then((res) => {
        setListing(res.data);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [key]);

  if (status === "loading") {
    return (
      <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
        <PageHeader />
        <div data-testid="listing-loading" className="max-w-6xl mx-auto px-6 py-32 flex items-center gap-3 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Loading listing…
        </div>
      </div>
    );
  }

  if (status === "error" || !listing) {
    return (
      <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
        <PageHeader />
        <div data-testid="listing-not-found" className="max-w-6xl mx-auto px-6 py-32 text-center">
          <Home className="mx-auto text-[#E890A7]" size={40} />
          <h1 className="font-display text-4xl mt-6">This listing has moved on</h1>
          <p className="text-slate-400 mt-3">It may have sold or been withdrawn. Explore the current Lagoon City collection.</p>
          <Link data-testid="listing-back-properties" to="/#properties" className="inline-block mt-8 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 hover:bg-[#F2A7BA] transition-colors">
            View current listings
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const gallery = (listing.images && listing.images.length ? listing.images : [listing.image]).filter(Boolean);
  const canonical = `${SITE}/listings/${listing.slug || listing.listing_id}`;
  const title = `${listing.address} — Lagoon City, ${listing.city || "Ramara"} | ${fmtPrice(listing.price)}`;
  const desc = (listing.description || `${listing.property_type || "Waterfront property"} for sale at ${listing.address}, Lagoon City, ${listing.city || "Ramara"}, Ontario. ${listing.beds ? listing.beds + " bed, " : ""}${listing.baths ? listing.baths + " bath. " : ""}Listed by Joselle Alves, Broker.`).slice(0, 300);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Residence",
    name: listing.address,
    description: desc,
    url: canonical,
    image: gallery.slice(0, 8),
    address: {
      "@type": "PostalAddress",
      streetAddress: listing.address,
      addressLocality: listing.city || "Ramara",
      addressRegion: "ON",
      addressCountry: "CA",
    },
    ...(listing.beds ? { numberOfRooms: listing.beds } : {}),
  };

  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={canonical} />
        {gallery[0] && <meta property="og:image" content={gallery[0]} />}
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <PageHeader />

      <main className="max-w-6xl mx-auto px-6 py-10 sm:py-14">
        <BackLink to="/#properties" label="Back to listings" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-8"
        >
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="inline-block rounded-full border border-emerald-400/30 bg-emerald-400/15 text-emerald-300 px-3 py-1 text-[10px] font-mono tracking-widest uppercase">
              {listing.status || "For Sale"}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#E890A7]/25 bg-[#0D0B0E]/60 text-[#F2A7BA] px-3 py-1 text-[10px] font-mono tracking-widest uppercase">
              <Clock3 size={10} /> {listedLabel(listing.listed_on)}
            </span>
          </div>
          <h1 data-testid="listing-title" className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight">
            {listing.address}
          </h1>
          <p className="flex items-center gap-2 text-slate-400 mt-3 font-light">
            <MapPin size={15} className="text-[#E890A7]" /> {listing.city || "Ramara"} · Lagoon City, Ontario
          </p>
          <p data-testid="listing-price" className="text-[#F2A7BA] font-semibold text-2xl mt-4">{fmtPrice(listing.price)}</p>
        </motion.div>

        {gallery.length > 0 && (
          <div className="mt-8">
            <div className="rounded-3xl overflow-hidden border border-[#E890A7]/12 bg-[#151218]">
              <img
                data-testid="listing-main-image"
                src={gallery[active]}
                alt={`${listing.address} — photo ${active + 1}`}
                className="w-full h-[22rem] sm:h-[34rem] object-cover"
              />
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 flex gap-3 overflow-x-auto pb-1" data-testid="listing-thumbnails">
                {gallery.map((g, i) => (
                  <button
                    key={i}
                    data-testid={`listing-thumb-${i}`}
                    onClick={() => setActive(i)}
                    className={`shrink-0 w-24 h-20 rounded-xl overflow-hidden border transition-all ${
                      i === active ? "border-[#E890A7]" : "border-[#E890A7]/15 opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img src={g} alt={`thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-10 grid lg:grid-cols-[1fr_20rem] gap-10">
          <div>
            <div className="flex flex-wrap gap-6 text-slate-300 border-y border-[#E890A7]/12 py-6">
              {listing.beds ? (
                <span className="flex items-center gap-2"><BedDouble size={18} className="text-[#E890A7]" /> {listing.beds} bedrooms</span>
              ) : null}
              {listing.baths ? (
                <span className="flex items-center gap-2"><Bath size={18} className="text-[#E890A7]" /> {listing.baths} bathrooms</span>
              ) : null}
              {sqftLabel(listing.sqft) ? (
                <span className="flex items-center gap-2"><Ruler size={18} className="text-[#E890A7]" /> {sqftLabel(listing.sqft)}</span>
              ) : null}
              {listing.property_type ? (
                <span className="flex items-center gap-2"><Home size={18} className="text-[#E890A7]" /> {listing.property_type}</span>
              ) : null}
            </div>

            {listing.description ? (
              <div className="mt-8">
                <h2 className="font-display text-2xl mb-4">About this home</h2>
                <p data-testid="listing-description" className="text-slate-400 font-light leading-relaxed whitespace-pre-line">
                  {listing.description}
                </p>
              </div>
            ) : (
              <p className="mt-8 text-slate-400 font-light">
                Reach out to Joselle for full details, a private tour, and the latest on this Lagoon City property.
              </p>
            )}
          </div>

          <aside className="h-fit rounded-[2rem] border border-[#E890A7]/25 bg-gradient-to-br from-[#1E1A23] to-[#151218] p-7">
            <div className="flex items-center gap-3">
              <img src="/assets/joselle-portrait.jpg" alt="Joselle Alves" className="w-14 h-14 rounded-full object-cover border border-[#E890A7]/30" />
              <div>
                <p className="font-display text-lg leading-none">Joselle Alves</p>
                <p className="text-[11px] text-slate-500 mt-1">Broker · Lagoon City specialist</p>
              </div>
            </div>
            <p className="text-sm text-slate-400 mt-5 font-light">Interested in {listing.address}? Book a private viewing or ask Joselle anything.</p>
            <Link
              data-testid="listing-enquire-btn"
              to="/#contact"
              onClick={() => trackEvent("enquire_listing", { listing: listing.address, price: listing.price })}
              className="mt-5 block text-center rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-6 py-3.5 hover:bg-[#F2A7BA] transition-colors"
            >
              Enquire about this home
            </Link>
            <a
              data-testid="listing-mls-link"
              href={listing.url || "https://www.sellwithjoselle.com"}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("view_mls_listing", { listing: listing.address })}
              className="mt-3 flex items-center justify-center gap-2 rounded-full border border-[#E890A7]/35 text-[#F2A7BA] px-6 py-3 text-sm hover:bg-[#E890A7]/10 transition-colors"
            >
              View on MLS® map <ArrowUpRight size={15} />
            </a>
            <a href="tel:+14167273558" data-testid="listing-call-link" onClick={() => trackEvent("contact_call", { source: "listing", listing: listing.address })} className="mt-4 block text-center text-sm text-slate-400 hover:text-[#F2A7BA] transition-colors">
              or call (416) 727-3558
            </a>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
